import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { socket } from "../api";
import { parseJWT } from "../utils/jwtUtils";
import cardBackImg from "../assets/Carta-card-bg2-removebg-preview.png";
import tableBg from "../assets/Game-background1.jpg";

/* ================= CARD UI ================= */

const CardBack = ({ small }) => (
  <img
    src={cardBackImg}
    alt="Card Back"
    className={`${small ? "w-10 h-16" : "w-20 h-28"} rounded-xl shadow-xl`}
  />
);

const CardFront = ({ card, disabled }) => {
  if (!card) return null; // 🔥 prevent crash
  if (card.hidden) return <CardBack />;

  const isRed = card.suit === "♥" || card.suit === "♦";

  return (
    <div
      className={`w-20 h-28 bg-white rounded-xl border shadow-xl flex flex-col justify-between p-2 relative transition ${disabled ? "opacity-40 grayscale" : ""}`}
    >
      <div
        className={`text-sm font-bold ${isRed ? "text-red-600" : "text-black"}`}
      >
        {card.rank}
        <br />
        {card.suit}
      </div>

      <div
        className={`absolute inset-0 flex items-center justify-center text-5xl opacity-20 ${isRed ? "text-red-500" : "text-black"}`}
      >
        {card.suit}
      </div>

      <div
        className={`text-sm font-bold self-end rotate-180 ${isRed ? "text-red-600" : "text-black"}`}
      >
        {card.rank}
        <br />
        {card.suit}
      </div>
    </div>
  );
};

/* ================= MAIN ================= */

export default function CrazyPlayGames() {
  const { roomId } = useParams();
  const [game, setGame] = useState(null);
  const [timer, setTimer] = useState(30);
  const [suitPicker, setSuitPicker] = useState(null);
  const [result, setResult] = useState(null);
  const [sevenPicker, setSevenPicker] = useState(null);

  const joinedRef = useRef(false);

  let phone = null;
  let name = null;

  try {
    const stored = localStorage.getItem("userInfo");

    if (stored) {
      const parsed = JSON.parse(stored);
      const userInfo = parseJWT(parsed?.token);

      phone = userInfo?.phone || null;
      name = userInfo?.username || null;
    }
  } catch (err) {
    console.error("JWT Parse Error:", err);
  }

  useEffect(() => {
    socket.on("joined-room", (data) => {
      //  console.log("🟢 joined-room:", data);
      setGame(data);
    });

    socket.on("game-started", (data) => {
      // console.log("🚀 game-started:", data);
      setGame(data);
    });

    socket.on("game-update", (data) => {
      // console.log("🔄 game-update:", data);
      setGame(data);
    });

    socket.on("game-over", (data) => {
      // console.log("🏁 game-over:", data);
      setResult(data.winner.phone === phone ? "WIN" : "LOSE");
    });

    return () => {
      socket.off("joined-room");
      socket.off("game-started");
      socket.off("game-update");
      socket.off("game-over");
    };
  }, []);

  useEffect(() => {
    if (!roomId || !phone) return;

    if (!joinedRef.current) {
      joinedRef.current = true;

      socket.emit("join-carta-room", {
        roomId,
        phone,
        name,
      });
    }
  }, [roomId, phone, name]);

  useEffect(() => {
    if (!game || typeof game.currentTurn !== "number") return;
    // console.log("⏳ Turn changed:", game.currentTurn);

    setTimer(30);

    const interval = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          // console.log("⏰ Auto draw triggered");
          socket.emit("draw-card", { roomId });
          return 30;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [game?.currentTurn]);

  useEffect(() => {
    if (!roomId || !phone) return;

    const handleReconnect = () => {
      //  console.log("🔁 Socket reconnected — rejoining room");

      socket.emit("join-carta-room", {
        roomId,
        phone,
        name,
      });
    };

    // run if socket reconnects
    socket.on("connect", handleReconnect);

    return () => {
      socket.off("connect", handleReconnect);
    };
  }, [roomId, phone, name]);

  if (!game) return <div className="text-white p-10">Waiting…</div>;

  const players = Array.isArray(game?.players) ? game.players : [];

  const me = players.find((p) => p.phone === phone);
  const opponent = players.find((p) => p.phone !== phone);

  const topCard = Array.isArray(game?.discardPile)
    ? game.discardPile[game.discardPile.length - 1]
    : null;

  const isMyTurn =
    players.length > 0 &&
    typeof game?.currentTurn === "number" &&
    players[game.currentTurn]?.phone === phone;

  if (!me) return <div className="text-white p-10">Waiting for game...</div>;

  if (!opponent)
    return <div className="text-white p-10">Waiting for opponent...</div>;

  if (!topCard) return <div className="text-white p-10">Preparing game...</div>;

  const myHand = Array.isArray(me?.hand) ? me.hand : [];

  const canPlay = (card) => {
    if (!card || !topCard) return false;

    return (
      card.rank === "8" ||
      card.rank === "J" ||
      card.suit === game.currentSuit ||
      card.rank === topCard.rank
    );
  };

  const hasPlayable = myHand.some(canPlay);

  return (
    <div
      className="min-h-screen flex flex-col justify-between text-white"
      style={{
        backgroundImage: `url(${tableBg})`,
        backgroundRepeat: "repeat", // ⭐ KEY FIX
        backgroundSize: "auto", // prevents scaling blur
      }}
    >
      {/* Opponent */}
      <div className="pt-6 text-center">
        <div
          className="
  inline-flex items-center justify-center gap-2
  px-3 py-1.5
  rounded-full
  bg-white/10 backdrop-blur-md
  border border-white/20
  shadow-[0_0_10px_rgba(0,0,0,0.5)]
"
        >
          <div
            className="
    bg-gradient-to-r from-purple-600 to-purple-500
    px-2 py-1
    rounded-full
    text-xs font-bold
    text-white
    shadow-md
  "
          >
            {(opponent.name || "P2").slice(0, 1)}
          </div>

          <p className="text-sm font-semibold text-white tracking-wide">
            {(opponent.name || "P2").slice(0, 5)}***
          </p>
        </div>

        <div className="flex justify-center flex-wrap gap-1 mt-4 max-w-xs mx-auto">
          {(opponent?.hand || []).map((_, i) => (
            <CardBack key={i} small />
          ))}
        </div>
      </div>

      {/* Center */}
      <div className="flex flex-col items-center">
        <div
          className={`px-4 py-1 rounded-lg my-2 font-semibold bg-white ${
            game.currentSuit === "♥" || game.currentSuit === "♦"
              ? "text-red-600"
              : "text-red-600"
          }`}
        >
          Current Suit:
          <span
            className={`ml-2 text-xl ${
              game.currentSuit === "♥" || game.currentSuit === "♦"
                ? "text-red-600"
                : ""
            }`}
          >
            {game.currentSuit}
          </span>
        </div>

        <div className="flex items-center gap-20 mb-6">
          <div
            onClick={() => isMyTurn && socket.emit("draw-card", { roomId })}
            className={`relative cursor-pointer ${!isMyTurn ? "opacity-40" : ""}`}
          >
            <CardBack />
            <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold">
              {game.deckCount}
            </div>
          </div>

          <CardFront card={topCard} />
        </div>

        <p className="text-sm">
          {isMyTurn
            ? `${(me.name || "P1").slice(0, 3)}**'s turn`
            : `${(opponent.name || "P2").slice(0, 3)}**'s turn`}
        </p>

        <div className="flex flex-col items-center mt-4">
          {/* Timer Bar Container */}
          <div className="w-64 h-10 bg-black/90 rounded-xl flex items-center px-3">
            {/* Progress */}
            <div
              className="h-4 bg-green-400 rounded-full transition-all duration-1000"
              style={{ width: `${(timer / 30) * 100}%` }}
            />
          </div>

          {/* Seconds */}
          <p className="text-white text-lg mt-2 font-semibold">{timer}s</p>
        </div>
      </div>

      {/* Hand */}
      <div className="pb-8">
        <div className="flex flex-wrap justify-center gap-4">
          {myHand.map((card, i) => {
            const playable = canPlay(card);

            return (
              <div
                key={i}
                onClick={() => {
                  if (!isMyTurn || !playable) return;

                  if (card.rank === "7") {
                    setSevenPicker(card);
                  } else if (card.rank === "8" || card.rank === "J") {
                    setSuitPicker(card);
                  } else {
                    socket.emit("play-card", { roomId, card });
                  }
                }}
                className="cursor-pointer"
              >
                <CardFront card={card} disabled={!playable || !isMyTurn} />
              </div>
            );
          })}
        </div>

        <div className="flex justify-center mt-6">
          <button
            disabled={!isMyTurn || hasPlayable}
            onClick={() => socket.emit("draw-card", { roomId })}
            className="bg-black/70 px-10 py-3 rounded-xl disabled:opacity-40 text-lg"
          >
            Draw Card
          </button>
        </div>
      </div>

      {/* Suit Picker */}
      {suitPicker && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center backdrop-blur-sm p-2">
          <div className="bg-white text-black w-[420px] rounded-2xl shadow-2xl p-6 animate-scaleIn">
            <div className="flex flex-col items-center text-center">
              <h2 className="text-2xl font-bold mb-2">
                Play {suitPicker.rank} of {suitPicker.suit}
              </h2>

              <p className="text-gray-500 mb-6">Select new suit:</p>
            </div>
            <div className="flex justify-center gap-4 mb-6">
              {["♠", "♥", "♦", "♣"].map((suit) => {
                const isRed = suit === "♥" || suit === "♦";

                return (
                  <button
                    key={suit}
                    onClick={() => {
                      socket.emit("play-card", {
                        roomId,
                        card: suitPicker,
                        chosenSuit: suit,
                      });
                      setSuitPicker(null);
                    }}
                    className={`w-16 h-20 rounded-xl shadow text-3xl ${
                      isRed ? "text-red-600" : "text-black"
                    } hover:scale-110 transition`}
                  >
                    {suit}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setSuitPicker(null)}
              className="w-full bg-red-600 text-white py-3 rounded-xl text-lg shadow hover:bg-red-500 transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {sevenPicker && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center backdrop-blur-sm p-2">
          <div className="bg-white text-black w-[420px] rounded-2xl shadow-2xl p-6 animate-scaleIn">
            <div className="flex flex-col items-center text-center">
              <h2 className="text-3xl font-bold mb-2">
                Play 7 of {sevenPicker.suit}
              </h2>

              <p className="text-gray-500 mb-6 text-lg">
                Choose how to play this card:
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {/* Play Alone */}
              <button
                onClick={() => {
                  socket.emit("play-card", {
                    roomId,
                    card: sevenPicker,
                    playMode: "single",
                  });
                  setSevenPicker(null);
                }}
                className="bg-blue-600 text-white py-3 rounded-xl text-lg shadow hover:bg-blue-500 transition"
              >
                Play alone (skip turn)
              </button>

              {/* Play With Suit */}
              <button
                onClick={() => {
                  socket.emit("play-card", {
                    roomId,
                    card: sevenPicker,
                    playMode: "chain",
                  });
                  setSevenPicker(null);
                }}
                className="bg-blue-600 text-white py-3 rounded-xl text-lg shadow hover:bg-blue-500 transition"
              >
                Play with other {sevenPicker.suit} cards
              </button>

              {/* Close */}
              <button
                onClick={() => setSevenPicker(null)}
                className="bg-red-600 text-white py-3 rounded-xl text-lg shadow hover:bg-red-500 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Result Popup */}
      {result && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 to-black text-white px-8 py-10 rounded-2xl shadow-2xl text-center animate-scaleIn">
            {/* Icon */}
            <div className="text-6xl mb-4">
              {result === "WIN" ? "🏆" : "💀"}
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold mb-2">
              {result === "WIN" ? "አሸንፈዋል!" : "ለጊዜው ተሸንፈዋል"}
            </h1>

            {/* Subtitle */}
            <p className="text-gray-400 mb-6">
              {result === "WIN"
                ? "እሄንን እድል ለመድገም ድጋሚ ይጫወቱ"
                : "በሚቀጥለው ዙር የተሻለ ዕድል ይገጥምዎታል"}
            </p>

            {/* Buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => (window.location.href = "/crazy-games")}
                className="bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-xl transition"
              >
                ወደ ቀጣይ ጨዋታ
              </button>

              {result === "LOSE" && (
                <button
                  onClick={() => (window.location.href = "/crazy-games")}
                  className="bg-red-600 hover:bg-red-500 px-6 py-3 rounded-xl transition"
                >
                  Play Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
